import EmptyState from "@/components/EmptyState";
import ErrorBoundary from "@/components/ErrorBoundary";
import Icon from "@/components/Icon";
import Progress from "@/components/Progress";
import Button from "@/components/Button";
import SectionHeading from "@/components/SectionHeading";
import SkeletonList from "@/components/Skeleton/SkeletonList";
import Stepper from "@/components/Stepper";
import { StepType } from "@/components/Stepper/Stepper";
import Tag from "@/components/Tag";
import { useCanManageInteractionsBool, useCanManageTasksBool } from "@/hooks/useCapabilities";
import AppLayout from "@/layouts/AppLayout";
import { toDateFormat } from "@/lib/dateHelpers";
import { showSnackbar } from "@/lib/snackbarUtils";
import { Path, useNavigate } from "@/router";
import { NoteNotableValues, Person } from "@/types";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { PersonObjectHeading } from "@/features/person/components/PersonObjectHeading";
import ConnectedPeople from "../_components/ConnectedPeople";
import TabLayout from "../_TabLayout";
import { usePersonErrorHandling } from "../../_components/usePersonErrorHandling";
import PersonNotFoundErrorBoundary from "../../_components/PersonNotFoundErrorBoundary";
import { getIcon, getLinkDescription, groupInteractions, handleSummary } from "@/components/interactions/_utils";
import InteractionDescriptionContent from "@/components/interactions/InteractionDescriptionContent";
import { Interaction, InteractionRecipient } from "@/components/interactions/types";
import AiCard from "@/components/AiCard";
import { GET_PERSON_INTERACTIONS_QUERY, SHARE_INTERACTION_MUTATION } from "@/components/interactions/queries";
import ShareInteractionModal from "@/components/interactions/modals/ShareInteractionModal";
import { useIsCompactLayout } from "@/hooks/useIsCompactLayout";
import { useTopActionBar, SideBar } from "@/layouts/AppLayout/TopActionBarContext";
import Breadcrumb from "@/components/Breadcrumb";
import Tooltip from "@/components/Tooltip";
import { PERSON_NOTES_QUERY, type PersonNotesQueryResponse } from "@/pages/people/[pgaId]/person/notes/queries";
import { INTERACTION_INSIGHTS_QUERY } from "./queries";

export const PAGE_SIZE = 10;

type PersonData = { person: Person };

interface InteractionGroupProps {
  monthYear: string;
  interactions: Interaction[];
  pgaId: string;
  noteCountsByInteraction?: Map<string, number>;
}

const InteractionGroup = ({ monthYear, interactions, pgaId, noteCountsByInteraction }: InteractionGroupProps) => {
  const [isExpanded, setExpended] = useState(true);
  const canManageInteractions = useCanManageInteractionsBool();
  const canManageTasks = useCanManageTasksBool();
  const showTaskIcon = canManageTasks;
  const [selectedInteraction, setSelectedInteraction] = useState<Interaction>();
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [shareVariables, setShareVariables] = useState<{
    recipients: InteractionRecipient[];
    notes: string;
  }>({
    recipients: [],
    notes: ""
  });
  const [shareInteractionMutation, { loading: sharingInteraction }] = useMutation(SHARE_INTERACTION_MUTATION);

  async function shareInteraction(interaction: Interaction) {
    try {
      const { data } = await shareInteractionMutation({
        variables: {
          input: {
            interaction: {
              id: interaction.id,
              description: interaction.lastMessage.content,
              owner: interaction.owner?.name,
              date: interaction.interactionDate,
              mode: interaction.mode,
              topic: interaction.categories[0],
              participants: [
                ...interaction.participants.map((participant) => `${participant.firstName} ${participant.lastName}`)
              ]
            },
            recipients: shareVariables.recipients.map((recipient) => ({
              name: recipient.name,
              email: recipient.email
            })),
            notes: shareVariables.notes,
            pgaId: pgaId
          }
        }
      });

      if (data?.shareInteraction?.success) {
        setShareModalOpen(false);
        showSnackbar("Interaction has been shared");
        setShareVariables({ recipients: [], notes: "" });
      } else {
        showSnackbar("There was a problem sharing this interaction. Please try again later");
      }
    } catch {
      showSnackbar("There was a problem sharing this interaction. Please try again later");
    }
  }

  const navigate = useNavigate();
  const showLink: Path = "/people/:pgaId/interactions/interactions/:interactionId";

  const stepsList = interactions.map((interaction) => {
    const { mode, owner, lastMessage, messagesCount, categories, interactionDate, showAiSummary } = interaction;
    const { isPrivate, haveAccess, content } = lastMessage;

    const summary = handleSummary(interaction);

    const participants = interaction.participants.map(({ profilePhoto }) => profilePhoto);
    const icon = getIcon(mode);
    const linkDescription = getLinkDescription(mode, true); // Capitalize the first letter
    const hasAccessToInteraction = !isPrivate || (isPrivate && haveAccess);

    const interactionNoteCount = noteCountsByInteraction?.get(interaction.id) ?? 0;

    const step: StepType = {
      headline: (
        <div className="flex flex-wrap gap-x-2 items-center relative">
          <span>
            <span className="text-sys-brand-primary">{linkDescription}</span>
            {owner?.name && ` with ${owner?.name}`}
          </span>
          {messagesCount >= 1 && ` (${messagesCount} messages)`}
          {interactionNoteCount > 0 && (
            <Tooltip
              variant="plain"
              supportingText={`${interactionNoteCount} ${interactionNoteCount === 1 ? "Note" : "Notes"}`}
              compactHoverArea
              anchorClassName=""
            >
              <Icon name="sticky_note_2" size={24} />
            </Tooltip>
          )}
          {showTaskIcon && (interaction.openTasksCount ?? 0) > 0 && (
            <Tooltip
              variant="plain"
              supportingText={`${interaction.openTasksCount} Open ${interaction.openTasksCount === 1 ? "Task" : "Tasks"}`}
              compactHoverArea
              anchorClassName=""
            >
              <Icon name="inventory" size={24} data-testid="interaction-tasks-icon" />
            </Tooltip>
          )}
          {interaction.lastMessage.isPrivate && !interaction.lastMessage.haveAccess && (
            <Tooltip variant="plain" supportingText="Restricted Access" compactHoverArea anchorClassName="">
              <Icon name="lock" size={24} data-testid="inaccessible-interaction-icon" />
            </Tooltip>
          )}
          {hasAccessToInteraction && canManageInteractions && (
            <div
              className="md:hidden hover:inline group-hover:inline"
              id={interaction.id}
              onClick={(event) => {
                event.stopPropagation();
                setSelectedInteraction(interaction);
                setShareModalOpen(true);
              }}
            >
              <div className="md:absolute -top-1 mt-0.5 md:mt-0">
                <Tooltip variant="plain" supportingText="Share Interaction" compactHoverArea anchorClassName="">
                  <Icon name="ios_share" size={24} className="mt-0.5" />
                </Tooltip>
              </div>
            </div>
          )}
        </div>
      ),
      supportingText: toDateFormat(new Date(interactionDate).toISOString().split("T")[0]) || "",
      icon,
      children: (
        <>
          {hasAccessToInteraction ? (
            <>
              <ConnectedPeople participants={participants} />
              <ul className="flex flex-wrap gap-2 items-center mt-3">
                {categories.map((category, index) => (
                  <li key={index}>
                    <Tag tagType="secondary">{category}</Tag>
                  </li>
                ))}
                {interaction.jobPost && (
                  <li>
                    <Tag tagType="tertiary" iconClass="text-sys-brand-primary">
                      <Icon name="business_center" size={16} className="mr-1" />
                      {interaction.jobPost.name}
                    </Tag>
                  </li>
                )}
              </ul>
              <div className="mt-3">
                <InteractionDescriptionContent content={showAiSummary ? summary : content} />
              </div>
            </>
          ) : null}
        </>
      )
    };

    return step;
  });

  return (
    <>
      <div className="pt-8 pb-3 flex items-center gap-2 cursor-pointer" onClick={() => setExpended(!isExpanded)}>
        {monthYear}
        <Icon name={isExpanded ? "expand_less" : "expand_more"} />
      </div>
      {isExpanded && (
        <Stepper
          onStepClick={(i) => {
            const interaction = interactions[i];
            const hasAccessToInteraction =
              !interaction.lastMessage.isPrivate ||
              (interaction.lastMessage.isPrivate && interaction.lastMessage.haveAccess);

            if (!hasAccessToInteraction) {
              showSnackbar("Access to this interaction is restricted");
              return;
            }

            navigate(showLink, {
              params: { pgaId, interactionId: interaction.id },
              state: { interaction }
            });
          }}
          variant="solid"
          stepsList={stepsList}
        />
      )}
      {shareModalOpen && selectedInteraction && (
        <ShareInteractionModal
          open={shareModalOpen}
          setOpen={setShareModalOpen}
          shareInteraction={() => shareInteraction(selectedInteraction)}
          isPrivate={selectedInteraction.lastMessage.isPrivate}
          shareVariables={shareVariables}
          setShareVariables={setShareVariables}
          disabled={sharingInteraction}
        />
      )}
    </>
  );
};

const InteractionsPage = ({
  interactions,
  setInteractions
}: {
  setSalesforceId: (value: string) => void;
  salesforceId: string | null;
  interactions: Interaction[];
  setInteractions: (value: Interaction[] | ((prevInteractions: Interaction[]) => Interaction[])) => void;
}) => {
  const { pgaId } = useParams();
  const [getInteractions, { data, loading, error }] = useLazyQuery<PersonData>(GET_PERSON_INTERACTIONS_QUERY, {
    fetchPolicy: "network-only"
  });
  const interactionsMap = groupInteractions(interactions);
  const pageInfo = data?.person?.interactions?.pageInfo;
  const hasMore = pageInfo && pageInfo.hasNextPage;
  const nextPage = pageInfo && pageInfo.endCursor;
  const [grabbedMore, setGrabbedMore] = useState(false);

  const { data: notesData } = useQuery<PersonNotesQueryResponse>(PERSON_NOTES_QUERY, {
    variables: {
      pgaId,
      first: 500,
      input: { notableType: [NoteNotableValues.INTERACTION] }
    },
    skip: !pgaId
  });

  const noteCountsByInteraction = useMemo(() => {
    const map = new Map<string, number>();
    if (!notesData?.personNotes?.nodes) return map;
    for (const note of notesData.personNotes.nodes) {
      map.set(note.notableId, (map.get(note.notableId) ?? 0) + 1);
    }
    return map;
  }, [notesData]);

  const {
    data: insightsData,
    loading: generatingInsightsSummary,
    error: insightsError
  } = useQuery(INTERACTION_INSIGHTS_QUERY, {
    variables: { pgaId: pgaId ?? "" },
    skip: !pgaId,
    fetchPolicy: "cache-and-network"
  });
  const insightSummary = insightsData?.insights?.interactionInsights ?? "";
  const showAiSummary = Boolean(insightSummary) || generatingInsightsSummary;

  useEffect(() => {
    if (insightsError) {
      console.error("Error loading interaction insights:", insightsError);
    }
  }, [insightsError]);

  useEffect(() => {
    if (pgaId) {
      getInteractions({ variables: { search: pgaId, first: PAGE_SIZE } });
    }
    return () => {
      setInteractions([]); // Cleanup on unmount
    };
  }, [getInteractions, pgaId, setInteractions]);

  useEffect(() => {
    const newInteractions = data?.person?.interactions?.nodes || [];

    setInteractions((prevInteractions) => {
      // If we have new data from Apollo, combine it with existing data
      if (newInteractions.length > 0) {
        // Combine previous interactions with new ones
        const combinedInteractions = [...prevInteractions, ...newInteractions];

        // Remove duplicates based on ID
        const uniqueInteractions = combinedInteractions.filter(
          (interaction, index, self) => index === self.findIndex((t) => t.id === interaction.id)
        );

        // Sort by interaction date (newest first)
        const sortedInteractions = uniqueInteractions.sort(
          (a, b) => new Date(b.interactionDate).getTime() - new Date(a.interactionDate).getTime()
        );

        return sortedInteractions;
      }

      // If no new data, keep existing interactions
      return prevInteractions;
    });
  }, [data?.person?.interactions?.nodes, setInteractions]);

  if (error || !pgaId) {
    return (
      <div data-testid="error">
        <ErrorBoundary
          variant="inline"
          title="Unable to Load Interactions"
          message={error?.message || "Missing required parameters"}
        />
      </div>
    );
  }

  if (!interactions.length && loading) {
    return (
      <div className="w-[500px] mt-8">
        <SkeletonList rows={10} />
      </div>
    );
  }

  if (!interactions.length && !loading) {
    return (
      <div data-testid="interactions-empty-state">
        <EmptyState
          iconName="3p"
          title="No Interactions"
          caption="There are no interactions associated with this person."
        />
      </div>
    );
  }

  return (
    <>
      {showAiSummary && (
        <div className="mt-6">
          <AiCard
            text={insightSummary}
            loading={generatingInsightsSummary}
            feedbackUrl={`https://docs.google.com/forms/d/e/1FAIpQLSc1yxNMVrq4NwBCSLVnIm6ILLziFQD0VR9T_3UqAEeCWWGNEQ/viewform?usp=pp_url&entry.2050300929=${encodeURIComponent(window.location.href)}&entry.470452171=${encodeURIComponent(insightSummary)}`}
            renderAsMarkdown
          />
        </div>
      )}
      <ul className="flex flex-col">
        {Object.entries(interactionsMap).map(([monthYear, interactions], i) => (
          <li key={i} data-testid={`interactions-month-group-${i}`}>
            <InteractionGroup
              monthYear={monthYear}
              interactions={interactions}
              pgaId={pgaId}
              noteCountsByInteraction={noteCountsByInteraction}
            />
          </li>
        ))}
      </ul>
      <div className="flex justify-center mt-6">
        {(loading || hasMore) && (
          <Button
            onClick={() => {
              getInteractions({
                variables: { search: pgaId, first: PAGE_SIZE, after: nextPage }
              });
              setGrabbedMore(true);
            }}
            variant="filledTonal"
            disabled={loading}
          >
            Load More Interactions {loading && <Progress className="ml-2" />}
          </Button>
        )}
        {!loading && !hasMore && grabbedMore && <p>No more past interactions to load</p>}
      </div>
    </>
  );
};

const InteractionsSectionHeading = () => {
  const canManageInteractions = useCanManageInteractionsBool();
  const { showSidebar } = useTopActionBar();
  const openGlobalInteraction = showSidebar(SideBar.CREATE_GLOBAL_INTERACTION);

  return (
    <div className="[&>div]:pb-0">
      <SectionHeading
        heading="Person/PGA Interactions"
        body="View tracked conversations, outreach, and other touchpoints with this person and PGA staff."
        actions={
          canManageInteractions
            ? [
                {
                  label: "Add Interaction",
                  onClick: openGlobalInteraction,
                  testId: "add-interaction-button",
                  icon: "add_comment"
                }
              ]
            : []
        }
      />
    </div>
  );
};

export default function InteractionsPageContainer() {
  const { isCompactLayout: isMobile } = useIsCompactLayout();
  const [salesforceId, setSalesforceId] = useState("");
  const [interactions, setInteractions] = useState<Interaction[]>([]);
  const { pgaId, navLinks, shouldRenderError } = usePersonErrorHandling();

  // Early return for error cases
  if (shouldRenderError) {
    return (
      <AppLayout navLinks={[]} interior bottomBar>
        <PersonNotFoundErrorBoundary />
      </AppLayout>
    );
  }

  return (
    <>
      <AppLayout navLinks={navLinks} interior bottomBar>
        {isMobile && (
          <Breadcrumb
            compactOnly
            compactTitle="Interactions"
            backLink={{ to: "/people/:pgaId", params: { pgaId: pgaId! } }}
          />
        )}
        <PersonObjectHeading pgaId={pgaId!} />
        <TabLayout>
          <InteractionsSectionHeading />
          <InteractionsPage
            setSalesforceId={setSalesforceId}
            salesforceId={salesforceId}
            interactions={interactions}
            setInteractions={setInteractions}
          />
        </TabLayout>
      </AppLayout>
    </>
  );
}
